import React, { useEffect, useState } from 'react';
import {
  RegisterOptions,
  useFormContext,
} from 'react-hook-form/dist/index.ie11';
import { CookbookPatternFormatNumberInputWrapper } from 'src/components/CookbookPatternFormatNumberInputWrapper/CookbookPatternFormatNumberInputWrapper';
import { determineFormat } from 'src/components/oneCorp/features/MealCredit/common/formFields/TextInput/TimeInput/TimeInput.utils';
import { IMealCreditFormFields } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { TIME_REGEX } from 'src/utilities/regexPatterns';

export interface TimeInputProps {
  label: string;
  name: string;
  id: string;
  disabled?: boolean;

  rules?: RegisterOptions;
}

export function TimeInput({
  label,
  name,
  id,
  disabled = false,
  rules = {},
}: TimeInputProps): JSX.Element {
  const { errors, control, watch } = useFormContext<IMealCreditFormFields>();
  const [format, setFormat] = useState('##:##');
  const value = watch(name);

  // We need to watch the value because the onInput handler is not called when the value is set programmatically (e.g. using setValue)
  // Since we are using setValue only with valid values, we update the format only in this case in order not to interfere with the onInput handler
  useEffect(() => {
    // If disabled, we directly determine the format based on the default value because the form value could have been already formatted with a different format
    const valueToDetermineFormat = disabled
      ? control.defaultValuesRef.current[name]
      : value;
    const isValid = TIME_REGEX.test(valueToDetermineFormat);
    if (isValid) {
      const newFormat = determineFormat(valueToDetermineFormat);
      setFormat(newFormat);
    }
  }, [value, disabled, control, name]);

  const onInputHandle = (e: React.FormEvent<HTMLInputElement>): void => {
    const value = (e.target as HTMLInputElement)?.value;
    if (!/\d/.test(value)) {
      return; // Early return for non-numeric input
    }

    const newFormat = determineFormat(value);
    setFormat(newFormat);
  };

  return (
    <CookbookPatternFormatNumberInputWrapper
      eager
      format={format}
      blockWidth
      onInput={onInputHandle}
      control={control}
      disabled={disabled}
      label={label}
      name={name}
      inputSize="medium"
      id={id}
      placeholder="h:mm"
      error={errors[name]}
      rules={{
        required: 'Required',
        pattern: {
          value: TIME_REGEX,
          message: 'Must be formatted as H:MM (ex. 8:40)',
        },
        ...rules,
      }}
    />
  );
}
